/**
 * The seeded random number generator every rule that needs chance draws from.
 *
 * sfc32: small, fast, no dependency, and -- the part that matters here --
 * exactly reproducible from a 32-byte seed on any machine. The island a player
 * walks must be the island the seed produces, because the seed is published
 * once the island has finalised and anyone may check (`docs/rules.md` section
 * 2). `Math.random` is never used anywhere in this package.
 *
 * This module has no notion of secrecy. Deriving the day's seed from
 * `SEED_SECRET` is `./seed.ts`, which is server-only.
 */

/** Bytes of seed material `createRng` expects; also the width of an HMAC-SHA256. */
export const SEED_BYTES = 32

export type Rng = {
  /** The next 32 unsigned bits. */
  uint32(): number
  /** A float in [0, 1). */
  next(): number
  /** A uniform integer in [0, maxExclusive), without modulo bias. */
  int(maxExclusive: number): number
  /** A uniform member of a non-empty list. */
  pick<T>(items: readonly T[]): T
  /** A member drawn in proportion to its weight. Weights need not sum to 1. */
  weighted<T>(items: readonly T[], weight: (item: T) => number): T
  /** A new array holding the same members in a uniformly random order. */
  shuffle<T>(items: readonly T[]): T[]
  /** True with the given probability, 0.5 by default. */
  bool(probability?: number): boolean
}

const readUint32 = (seed: Uint8Array, offset: number): number =>
  ((seed[offset]! | (seed[offset + 1]! << 8) | (seed[offset + 2]! << 16)) +
    seed[offset + 3]! * 0x1000000) >>>
  0

/**
 * Build a generator from 32 bytes.
 *
 * sfc32 carries 128 bits of state, and a seed is twice that. The second half is
 * stirred in a word at a time rather than exclusive-ored over the first: a fold
 * would collapse any seed whose halves match -- every byte the same, say -- to
 * a state of zeroes, and two such seeds would then produce the same island.
 * The first outputs of a freshly seeded sfc32 correlate with the seed, so
 * twelve are discarded.
 */
export function createRng(seed: Uint8Array): Rng {
  if (seed.length !== SEED_BYTES) {
    throw new Error(`a seed is ${SEED_BYTES} bytes, not ${seed.length}`)
  }

  let a = readUint32(seed, 0)
  let b = readUint32(seed, 4)
  let c = readUint32(seed, 8)
  let d = readUint32(seed, 12)

  const uint32 = (): number => {
    a >>>= 0
    b >>>= 0
    c >>>= 0
    d >>>= 0
    let t = (a + b) | 0
    a = b ^ (b >>> 9)
    b = (c + (c << 3)) | 0
    c = (c << 21) | (c >>> 11)
    d = (d + 1) | 0
    t = (t + d) | 0
    c = (c + t) | 0
    return t >>> 0
  }

  for (let offset = 16; offset < SEED_BYTES; offset += 4) {
    a = (a ^ readUint32(seed, offset)) >>> 0
    uint32()
  }
  for (let i = 0; i < 12; i += 1) uint32()

  const int = (maxExclusive: number): number => {
    if (!Number.isInteger(maxExclusive) || maxExclusive <= 0) {
      throw new Error(`int() needs a positive whole bound, not ${maxExclusive}`)
    }
    // Rejection sampling: a plain modulo would favour the low values whenever
    // 2^32 is not a multiple of the bound, and a biased draw among relic
    // stakers is exactly the unfairness the rules forbid.
    const limit = Math.floor(0x100000000 / maxExclusive) * maxExclusive
    let drawn = uint32()
    while (drawn >= limit) drawn = uint32()
    return drawn % maxExclusive
  }

  const rng: Rng = {
    uint32,
    next: () => uint32() / 0x100000000,
    int,
    pick: (items) => {
      if (items.length === 0) throw new Error('pick() needs a non-empty list')
      return items[int(items.length)]!
    },
    weighted: (items, weight) => {
      if (items.length === 0) throw new Error('weighted() needs a non-empty list')
      let total = 0
      for (const item of items) {
        const value = weight(item)
        if (!(value >= 0) || !Number.isFinite(value)) {
          throw new Error(`a weight must be a finite number at or above zero, not ${value}`)
        }
        total += value
      }
      if (total <= 0) throw new Error('weighted() needs at least one positive weight')
      let target = rng.next() * total
      for (const item of items) {
        target -= weight(item)
        if (target < 0) return item
      }
      // Only reachable through floating-point drift at the very top of the range.
      return items[items.length - 1]!
    },
    shuffle: (items) => {
      const copy = [...items]
      for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = int(i + 1)
        const swap = copy[i]!
        copy[i] = copy[j]!
        copy[j] = swap
      }
      return copy
    },
    bool: (probability = 0.5) => rng.next() < probability,
  }

  return rng
}

/** Parse 64 hex characters into seed bytes. `game_days.seed_hex` is stored so. */
export function seedFromHex(hex: string): Uint8Array {
  if (!/^[0-9a-fA-F]+$/.test(hex) || hex.length !== SEED_BYTES * 2) {
    throw new Error(`a seed is ${SEED_BYTES * 2} hex characters`)
  }
  const seed = new Uint8Array(SEED_BYTES)
  for (let i = 0; i < SEED_BYTES; i += 1) {
    seed[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  }
  return seed
}

export function seedToHex(seed: Uint8Array): string {
  return [...seed].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}
